import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import {
  Workflow,
  Plus,
  Play,
  Pause,
  Trash2,
  Edit,
  Copy,
  Check,
  X,
  Mail,
  Bell,
  ShoppingCart,
  User,
  Calendar,
  Lock
} from 'lucide-react';
import toast from 'react-hot-toast';

const triggerOptions = [
  { id: 'order_placed', name: 'Order Placed', icon: ShoppingCart },
  { id: 'user_registered', name: 'User Registered', icon: User },
  { id: 'form_submitted', name: 'Form Submitted', icon: Check },
  { id: 'scheduled', name: 'Scheduled', icon: Calendar },
];

const actionOptions = [
  { id: 'send_email', name: 'Send Email', icon: Mail },
  { id: 'send_notification', name: 'Send Notification', icon: Bell },
  { id: 'update_database', name: 'Update Database', icon: Check },
  { id: 'webhook', name: 'Webhook', icon: Workflow },
];

const sampleWorkflows = [
  {
    id: '1',
    name: 'Welcome Email',
    description: 'Send welcome email to new users',
    trigger: 'user_registered',
    action: 'send_email',
    status: 'active',
    lastRun: '2 hours ago'
  },
  {
    id: '2',
    name: 'Order Confirmation',
    description: 'Send order confirmation email',
    trigger: 'order_placed',
    action: 'send_email',
    status: 'active',
    lastRun: '5 minutes ago'
  },
  {
    id: '3',
    name: 'Daily Report',
    description: 'Send daily analytics report',
    trigger: 'scheduled',
    action: 'send_email',
    status: 'paused',
    lastRun: '1 day ago'
  }
];

const Workflows = () => {
  const { user } = useAuth();
  const { hasFeature } = useSubscription();
  const [workflows, setWorkflows] = useState(sampleWorkflows);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWorkflow, setNewWorkflow] = useState({
    name: '',
    description: '',
    trigger: '',
    action: ''
  });

  const hasWorkflows = hasFeature('workflows');

  const handleCreateWorkflow = () => {
    if (!newWorkflow.name || !newWorkflow.trigger || !newWorkflow.action) {
      toast.error('Please fill in all required fields');
      return;
    }

    const workflow = {
      id: Date.now().toString(),
      ...newWorkflow,
      status: 'active',
      lastRun: 'Never'
    };

    setWorkflows([...workflows, workflow]);
    toast.success('Workflow created successfully!');
    setShowCreateModal(false);
    setNewWorkflow({ name: '', description: '', trigger: '', action: '' });
  };

  const toggleWorkflow = (id) => {
    setWorkflows(workflows.map(w => 
      w.id === id 
        ? { ...w, status: w.status === 'active' ? 'paused' : 'active' }
        : w
    ));
    toast.success('Workflow status updated');
  };

  const deleteWorkflow = (id) => {
    setWorkflows(workflows.filter(w => w.id !== id));
    toast.success('Workflow deleted');
  };

  if (!hasWorkflows) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Lock className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Workflows Locked</h2>
        <p className="text-gray-500 text-center max-w-md mb-6">
          Workflow automation is available on Professional and Enterprise plans.
        </p>
        <button
          onClick={() => window.location.href = '/plans'}
          className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
        >
          Upgrade Plan
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workflows</h1>
          <p className="text-gray-500">Automate tasks and streamline your business</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
        >
          <Plus className="w-5 h-5" />
          Create Workflow
        </button>
      </div>

      {/* Workflows List */}
      {workflows.length > 0 ? (
        <div className="space-y-4">
          {workflows.map((workflow) => (
            <div
              key={workflow.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                    <Workflow className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{workflow.name}</h3>
                    <p className="text-sm text-gray-500">{workflow.description}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        workflow.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {workflow.status}
                      </span>
                      <span className="text-xs text-gray-400">
                        Last run: {workflow.lastRun}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleWorkflow(workflow.id)}
                    className={`p-2 rounded-lg transition-colors ${
                      workflow.status === 'active'
                        ? 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'
                        : 'bg-green-50 text-green-600 hover:bg-green-100'
                    }`}
                  >
                    {workflow.status === 'active' ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => {/* Edit */}}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => deleteWorkflow(workflow.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Workflow Flow */}
              <div className="mt-4 flex items-center gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
                  {React.createElement(
                    triggerOptions.find(t => t.id === workflow.trigger)?.icon || User,
                    { className: 'w-4 h-4 text-gray-500' }
                  )}
                  <span className="text-sm text-gray-600">
                    {triggerOptions.find(t => t.id === workflow.trigger)?.name || workflow.trigger}
                  </span>
                </div>
                <div className="text-gray-400">→</div>
                <div className="flex items-center gap-2 px-4 py-2 bg-primary-50 rounded-lg">
                  {React.createElement(
                    actionOptions.find(a => a.id === workflow.action)?.icon || Mail,
                    { className: 'w-4 h-4 text-primary-600' }
                  )}
                  <span className="text-sm text-primary-600">
                    {actionOptions.find(a => a.id === workflow.action)?.name || workflow.action}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
          <Workflow className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No workflows yet</h3>
          <p className="text-gray-500 mb-4">Create your first workflow to automate tasks</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
          >
            <Plus className="w-5 h-5" />
            Create Workflow
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Create New Workflow
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Workflow Name *
                </label>
                <input
                  type="text"
                  value={newWorkflow.name}
                  onChange={(e) => setNewWorkflow({...newWorkflow, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., Welcome Email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={newWorkflow.description}
                  onChange={(e) => setNewWorkflow({...newWorkflow, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="What does this workflow do?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trigger *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {triggerOptions.map((trigger) => (
                    <button
                      key={trigger.id}
                      onClick={() => setNewWorkflow({...newWorkflow, trigger: trigger.id})}
                      className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                        newWorkflow.trigger === trigger.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <trigger.icon className={`w-5 h-5 ${
                        newWorkflow.trigger === trigger.id ? 'text-primary-600' : 'text-gray-400'
                      }`} />
                      <span className="text-sm font-medium">{trigger.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Action *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {actionOptions.map((action) => (
                    <button
                      key={action.id}
                      onClick={() => setNewWorkflow({...newWorkflow, action: action.id})}
                      className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                        newWorkflow.action === action.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <action.icon className={`w-5 h-5 ${
                        newWorkflow.action === action.id ? 'text-primary-600' : 'text-gray-400'
                      }`} />
                      <span className="text-sm font-medium">{action.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateWorkflow}
                className="flex-1 py-2 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
              >
                Create Workflow
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Workflows;
